import HomeLayout from 'components/home/Layout';
import DashboardLayout from 'components/dashboard/Layout';
import { PerformanceTest } from 'interfaces/performanceTest';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { getReport } from 'services/report';
import { useToast } from 'hooks/useToast';
import SEO from 'components/home/SEO';
import PerformanceReport from 'components/dashboard/PerformanceReport';
import Spinner from 'components/icons/Spinner';
import Button from 'components/shared/Button';
import { useAuth } from 'hooks/useAuth';
import Link from 'next/link';

interface Props {
  report?: string;
}

const getReportUuid = (query) => {
  const slugValue = (query.slug || [])[0];
  return slugValue === 'demo'
    ? 'bdd8ce59-8c93-431f-aea9-d53d3ad03182'
    : slugValue;
};

const ReportPage: React.FC<Props> = () => {
  const { user } = useAuth();
  const router = useRouter();
  const { addToast } = useToast();
  const reportUuid = getReportUuid(router.query);

  const [perfReport, setPerfReport] = useState<PerformanceTest>();
  const [loading, setLoading] = useState<boolean>(true);

  const SelectedLayout = user ? DashboardLayout : HomeLayout;

  useEffect(() => {
    if (reportUuid) {
      getReport(reportUuid)
        .then((report) => {
          setPerfReport(report);
          setLoading(false);
        })
        .catch((error) => {
          console.error(error);
          setLoading(false);
          addToast({
            title: 'Unknown report URL',
            description: `Could not find a report with ID ${reportUuid}`,
            type: 'error',
          });
        });
    }
  }, [addToast, reportUuid]);

  const Loading = () => (
    <div className="h-2/3 flex flex-col">
      <Spinner width="40" className="flex-1 m-auto mt-6 animate-spin" />
    </div>
  );

  const NotFound = () => (
    <div className="h-full mt-10 sm:mt-16 py-10 max-w-2xl mx-auto bg-white rounded-lg flex flex-col text-center text-sm">
      <img
        className="object-cover mx-auto rounded-lg w-2/3 max-w-sm"
        alt="blank.canvas"
        src="/img/undraw_done_checking_re_6vyx.jpg"
        loading="lazy"
      />
      <div className="my-5 px-5">
        Could not find the report.{' '}
        <Link
          href={`/login?returnTo=${router.asPath}`}
          className="text-royal-blue-600"
        >
          Login
        </Link>{' '}
        for access or create a new report.
      </div>
      <div className="mx-auto">
        <a
          href="https://docs.latencylingo.com/docs/getting-started"
          target="_self"
        >
          <Button title="Learn how to create a report" />
        </a>
      </div>
    </div>
  );

  return (
    <>
      <SEO prefix="Report" />
      <SelectedLayout>
        {loading && <Loading />}
        {!loading && !perfReport && <NotFound />}
        {!loading && perfReport && (
          <PerformanceReport perfReport={perfReport} />
        )}
      </SelectedLayout>
    </>
  );
};

export default ReportPage;
